// Nadrama <https://nadrama.com>
// Copyright Nadrama Pty Ltd
// All rights reserved.
import { For, Show } from "solid-js";
import { useLocation } from "@tanstack/solid-router";
import type {
  ClusterBody,
  MemberBody,
  NotificationBody,
  ProviderBody,
  SessionBody,
} from "./api/generated";
import { AppLink } from "./navigation";

type TableItem =
  | ClusterBody
  | MemberBody
  | NotificationBody
  | ProviderBody
  | SessionBody;
export function Rows(p: { rows: TableItem[]; onDisconnect?: (id: string) => Promise<void> }) {
  const email = (r: TableItem) => ("email" in r ? r.email : undefined);
  const kind = (r: TableItem) => ("kind" in r ? r.kind : undefined);
  const name = (r: TableItem) =>
    ("name" in r ? r.name : undefined) ??
    email(r) ??
    ("verified" in r ? r.awsAccountId ?? r.googleProjectId : undefined);
  const region = (r: TableItem) => ("region" in r ? r.region : undefined);
  const status = (r: TableItem) =>
    "status" in r
      ? r.status
      : "verified" in r
        ? r.verified
          ? "Verified"
          : "Connected"
        : "Active";
  const rowLocation = useLocation();
  const route = () => rowLocation().pathname.split("/").filter(Boolean).at(-1);
  const emptyMessage = () =>
    route() === "providers"
      ? "No providers currently connected"
      : route() === "clusters"
        ? "No clusters currently exist"
        : undefined;
  const clusters = () =>
    p.rows.filter((row): row is ClusterBody => "nodes" in row);
  const GenericRows = () => (
    <>
      <div class="desktop-table panel overflow-x-auto">
        <table class="table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Kind</th>
              <th>Region</th>
              <th>Status</th>
              <Show when={p.onDisconnect}><th class="text-right">Actions</th></Show>
            </tr>
          </thead>
          <tbody>
            <For each={p.rows}>
              {(r) => (
                <tr>
                  <td class="font-medium">{name(r)}</td>
                  <td>{kind(r)}</td>
                  <td>{region(r) ?? "—"}</td>
                  <td>
                    <span class="status text-success">{status(r)}</span>
                  </td>
                  <Show when={p.onDisconnect}>
                    <td class="text-right"><button class="btn btn-ghost btn-sm" onClick={() => p.onDisconnect?.(r.id)}>Disconnect</button></td>
                  </Show>
                </tr>
              )}
            </For>
          </tbody>
        </table>
      </div>
      <div class="mobile-card gap-3">
        <For each={p.rows}>
          {(r) => (
            <article class="panel p-4">
              <h2 class="font-bold">{name(r)}</h2>
              <p class="mt-2 text-sm">
                {kind(r)} · {region(r)}
              </p>
              <span class="status mt-3 text-sm text-success">{status(r)}</span>
              <Show when={p.onDisconnect}>
                <button class="btn btn-ghost btn-sm mt-3" onClick={() => p.onDisconnect?.(r.id)}>Disconnect</button>
              </Show>
            </article>
          )}
        </For>
      </div>
    </>
  );
  const ClusterRows = () => (
    <>
      <div class="desktop-table panel overflow-x-auto">
        <table class="table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Status</th>
              <th class="text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            <For each={clusters()}>
              {(cluster) => {
                const deleted = cluster.status.toLowerCase() === "deleted";
                return (
                  <tr>
                    <td class="font-medium">{cluster.name}</td>
                    <td>
                      <span
                        class={`status ${deleted ? "text-slate-500" : "text-success"}`}
                      >
                        {cluster.status}
                      </span>
                    </td>
                    <td>
                      <Show when={!deleted}>
                        <div class="flex justify-end gap-2">
                          <AppLink
                            class="btn btn-ghost btn-sm"
                            to={`${rowLocation().pathname}/${cluster.id}/status`}
                          >
                            View
                          </AppLink>
                          <AppLink
                            class="btn btn-ghost btn-sm"
                            to={`${rowLocation().pathname}/${cluster.id}/update`}
                          >
                            Edit
                          </AppLink>
                        </div>
                      </Show>
                    </td>
                  </tr>
                );
              }}
            </For>
          </tbody>
        </table>
      </div>
      <div class="mobile-card gap-3">
        <For each={clusters()}>
          {(cluster) => {
            const deleted = cluster.status.toLowerCase() === "deleted";
            return (
              <article class="panel p-4">
                <h2 class="font-bold">{cluster.name}</h2>
                <span
                  class={`status mt-3 text-sm ${deleted ? "text-slate-500" : "text-success"}`}
                >
                  {cluster.status}
                </span>
                <Show when={!deleted}>
                  <div class="mt-4 flex gap-2">
                    <AppLink
                      class="btn btn-ghost btn-sm"
                      to={`${rowLocation().pathname}/${cluster.id}/status`}
                    >
                      View
                    </AppLink>
                    <AppLink
                      class="btn btn-ghost btn-sm"
                      to={`${rowLocation().pathname}/${cluster.id}/update`}
                    >
                      Edit
                    </AppLink>
                  </div>
                </Show>
              </article>
            );
          }}
        </For>
      </div>
    </>
  );
  return (
    <Show
      when={p.rows.length > 0}
      fallback={
        <Show when={emptyMessage()} fallback={<GenericRows />}>
          {(message) => (
            <>
              <hr class="border-slate-200" />
              <p class="py-10 text-center text-slate-400">{message()}</p>
            </>
          )}
        </Show>
      }
    >
      <Show
        when={route() === "clusters" && clusters().length === p.rows.length}
        fallback={<GenericRows />}
      >
        <ClusterRows />
      </Show>
    </Show>
  );
}
